<script setup>
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  inventaire:           { type: Object,  default: () => ({}) },
  type_inventaires:     { type: Array,   default: () => [] },
  type_taux:            { type: Array,   default: () => [] },
  show_type_inventaire: { type: Boolean, default: false },
  show_type_taux:       { type: Boolean, default: false },
  errors:               { type: Object,  default: () => ({}) },
})

const form = useForm({
  code:               props.inventaire.code               || '',
  nom:                props.inventaire.nom                || '',
  type_inventaire_id: props.inventaire.type_inventaire_id || '',
  quantite:           props.inventaire.quantite           || '',
  taux:               props.inventaire.taux               || '',
  type_taux_id:       props.inventaire.type_taux_id       || '',
  description:        props.inventaire.description        || '',
})

const { error: toastError } = useToast()

const fieldLabels = {
  code:               'Code',
  nom:                'Nom',
  quantite:           'Quantité',
  taux:               'Taux',
  type_inventaire:    'Type',
  type_inventaire_id: 'Type',
  type_taux:          'Unité de taux',
  type_taux_id:       'Unité de taux',
  description:        'Description',
}

function showErrors() {
  const merged = { ...props.errors, ...form.errors }
  Object.entries(merged).forEach(([k, v]) => {
    const label = fieldLabels[k] || k
    const msgs = Array.isArray(v) ? v : [v]
    msgs.forEach(m => toastError(`${label} : ${m}`))
  })
}

function submit() {
  form.transform(data => ({ inventaire: data })).post('/inventaires', {
    preserveScroll: true,
    onError: () => showErrors(),
  })
}

function cancel() {
  window.location.href = '/inventaires'
}

const fieldError = (field) => {
  const aliases = {
    type_inventaire_id: 'type_inventaire',
    type_taux_id:       'type_taux',
  }
  const alt = aliases[field]
  return form.errors[field]
    || props.errors[field]?.[0]
    || (alt && (form.errors[alt] || props.errors[alt]?.[0]))
    || null
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Inventaires &rsaquo; Nouvel item
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-4xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Nouvel item d'inventaire
            </h1>
          </div>
          <a
            href="/inventaires"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Form Card -->
        <div class="bg-surface-container rounded-xl p-8 border-l-4 border-primary shadow-2xl shadow-black/40">
          <form @submit.prevent="submit" class="space-y-8">

            <!-- Code + Nom -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Code <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.code"
                  type="text"
                  placeholder="ex. INV-001"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  :class="fieldError('code') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('code')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('code') }}</p>
              </div>
              <div class="md:col-span-2">
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Nom <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.nom"
                  type="text"
                  placeholder="Description courte de l'item"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('nom') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('nom')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('nom') }}</p>
              </div>
            </div>

            <!-- Type + Quantité -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div v-if="show_type_inventaire">
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Type
                </label>
                <div class="relative">
                  <select
                    v-model="form.type_inventaire_id"
                    class="w-full bg-surface-container-highest border-none rounded-md text-on-surface px-4 py-3 focus:ring-2 focus:ring-primary/20 appearance-none transition-all outline-none"
                    :class="fieldError('type_inventaire_id') ? 'ring-2 ring-error/40' : ''"
                  >
                    <option value="">— Sélectionner —</option>
                    <option v-for="t in type_inventaires" :key="t.id" :value="t.id">
                      {{ t.code }}{{ t.description ? ' — ' + t.description : '' }}
                    </option>
                  </select>
                  <span class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-outline material-symbols-outlined text-base">expand_more</span>
                </div>
                <p v-if="fieldError('type_inventaire_id')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('type_inventaire_id') }}</p>
              </div>

              <div :class="show_type_inventaire ? '' : 'md:col-span-2'">
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Quantité
                </label>
                <input
                  v-model="form.quantite"
                  type="number"
                  step="any"
                  placeholder="0"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  :class="fieldError('quantite') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('quantite')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('quantite') }}</p>
              </div>
            </div>

            <!-- Taux + unité -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Taux ($)
                </label>
                <input
                  v-model="form.taux"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  :class="fieldError('taux') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('taux')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('taux') }}</p>
              </div>

              <div v-if="show_type_taux">
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Unité de taux
                </label>
                <div class="relative">
                  <select
                    v-model="form.type_taux_id"
                    class="w-full bg-surface-container-highest border-none rounded-md text-on-surface px-4 py-3 focus:ring-2 focus:ring-primary/20 appearance-none transition-all outline-none"
                    :class="fieldError('type_taux_id') ? 'ring-2 ring-error/40' : ''"
                  >
                    <option value="">— Sélectionner —</option>
                    <option v-for="t in type_taux" :key="t.id" :value="t.id">
                      {{ t.code }}{{ t.periode ? ' — ' + t.periode : '' }}
                    </option>
                  </select>
                  <span class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-outline material-symbols-outlined text-base">expand_more</span>
                </div>
                <p v-if="fieldError('type_taux_id')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('type_taux_id') }}</p>
              </div>
            </div>

            <!-- Description -->
            <div>
              <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                Description
              </label>
              <textarea
                v-model="form.description"
                rows="4"
                placeholder="Description détaillée de l'item..."
                class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-none"
              ></textarea>
            </div>

            <!-- Actions -->
            <div class="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                @click="cancel"
                class="px-6 py-3 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm"
              >
                Annuler
              </button>
              <button
                type="submit"
                :disabled="form.processing"
                class="px-8 py-3 machined-sheen text-on-primary font-black rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
              >
                <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                <span v-else class="material-symbols-outlined text-base">add_circle</span>
                Créer l'item
              </button>
            </div>

          </form>
        </div>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
